
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/contexts/unified-permissions';

// Define types for the edge function response
interface AICostData {
  cost_aggregates: string;
  cost_details: string;
  total_cost: number;
  total_tokens: number;
  total_users: number;
}

interface EdgeFunctionResponse {
  success: boolean;
  data?: AICostData;
  error?: string;
}

interface ChartDataItem {
  date: string;
  cost: number;
}

interface CostDetailItem {
  id: string;
  timestamp: string;
  model: string;
  tokens: number;
  cost: number;
  user_id?: string;
  email: string;
  feature: string;
}

// Raw JSON structures returned by the edge function
interface RawCostAggregate {
  period_start: string;
  total_cost_usd: number;
}

interface RawCostDetail {
  id: string;
  created_at: string;
  model: string;
  tokens_used: number;
  cost_usd: number;
  user_id?: string;
  email?: string;
  feature_type?: string;
}

export function useAICostData(timeRange: '7d' | '30d' | '90d' | 'all' = '30d') {
  const { user } = usePermissions();
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['ai-cost-data', timeRange, user?.id],
    queryFn: async () => {
      if (!user?.id) {
        throw new Error('User must be authenticated to access AI cost data');
      }
      
      // Call the get-ai-cost edge function
      const { data: responseData, error } = await supabase.functions.invoke('get-ai-cost', {
        body: JSON.stringify({ time_range: timeRange })
      });
      
      if (error) {
        throw new Error(`Failed to fetch AI cost data: ${error.message}`);
      }
      
      const response = responseData as EdgeFunctionResponse;
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch AI cost data');
      }
      
      if (!response.data) {
        return {
          chartData: [],
          costDetails: [],
          totalCost: 0,
          totalTokens: 0,
          totalUsers: 0
        };
      }
      
      console.log('Successfully fetched data from edge function:', response.data);
      return processData(response.data);
    },
    enabled: !!user?.id,
    retry: 1, // Limit retries to avoid excessive function calls
    retryDelay: 1000 // Wait 1 second before retrying
  });
  
  function processData(responseData: AICostData) {
    if (!responseData) {
      return {
        chartData: [],
        costDetails: [],
        totalCost: 0,
        totalTokens: 0,
        totalUsers: 0
      };
    }
    
    let rawChartData: RawCostAggregate[] = [];
    let rawCostDetails: RawCostDetail[] = [];
    
    try {
      // The RPC function returns JSON strings that need to be parsed
      if (responseData.cost_aggregates) {
        rawChartData = JSON.parse(responseData.cost_aggregates) as RawCostAggregate[];
      }

      if (responseData.cost_details) {
        rawCostDetails = JSON.parse(responseData.cost_details) as RawCostDetail[];
      }
      
      // Format the chart data for display
      const chartData: ChartDataItem[] = rawChartData
        .map((item: RawCostAggregate) => ({
          date: new Date(item.period_start).toLocaleDateString(),
          cost: parseFloat((item.total_cost_usd || 0).toFixed(4))
        }))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      // Format the cost details
      const costDetails: CostDetailItem[] = rawCostDetails.map((item: RawCostDetail) => ({
        id: item.id,
        timestamp: item.created_at,
        model: item.model,
        tokens: item.tokens_used,
        cost: parseFloat((item.cost_usd || 0).toFixed(4)),
        user_id: item.user_id,
        email: item.email || 'Unknown',
        feature: item.feature_type || 'Unknown'
      }));
      
      return {
        chartData,
        costDetails,
        totalCost: responseData.total_cost || 0,
        totalTokens: responseData.total_tokens || 0,
        totalUsers: responseData.total_users || 0
      };
    } catch (parseError: unknown) {
      console.error('Error parsing AI cost data:', parseError);
      if (parseError instanceof Error) {
        throw new Error(`Failed to parse AI cost data: ${parseError.message}`);
      }
      throw new Error('Failed to parse AI cost data');
    }
  }
  
  return {
    chartData: data?.chartData || [],
    costDetails: data?.costDetails || [],
    totalCost: data?.totalCost || 0,
    totalTokens: data?.totalTokens || 0,
    totalUsers: data?.totalUsers || 0,
    isLoading,
    error,
    refetch
  };
}
